import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';
import { dashboardAction, getMeta, makeDebouncedLoad } from '../../../../../resources/js/helpers';

export default class extends Controller {
    connect() {
        this.selectElement = this.element.querySelector('.form-select');

        if (!this.selectElement.tomselect) {
            this.optionsLabel = this.selectElement.dataset.optionsLabel;
            this.optionsValue = this.selectElement.dataset.optionsValue;
            this.optionsUrl = this.selectElement.dataset.optionsUrl;
            this.isMultiple = this.selectElement.dataset.optionsMultiple === '1';
            this.page = 1;
            this.table = this.element.closest('[data-controller="data-table"]');

            this.plugins = ['virtual_scroll'];

            this.select = new TomSelect(this.selectElement, {
                labelField: this.optionsLabel,
                valueField: this.optionsValue,
                searchField: [],
                create: false,
                preload: 'focus',
                highlight: false,
                maxOptions: null,
                loadThrottle: null,
                plugins: this.plugins,
                firstUrl: (query) => {
                    return this.optionsUrl + '?search=' + encodeURIComponent(query) + '&page=' + this.page + '&template_model=' + this.table.dataset.modelClass;
                },
                load: makeDebouncedLoad((query, callback) => {
                    fetch(this.select.getUrl(query))
                        .then(response => response.json())
                        .then(json => {
                            if (json.hasMore) {
                                this.page++;
                                const nextUrl = this.optionsUrl + '?search=' + encodeURIComponent(query) + '&page=' + this.page;
                                this.select.setNextUrl(query, nextUrl);
                            }

                            callback(json.entries);
                        })
                        .catch((e) => {
                            console.error(e);
                            callback();
                        });
                }),
                shouldLoad: () => {
                    return true;
                },
                onType: () => {
                    this.page = 1;
                },
                render: {
                    option: (data) => {
                        const div = document.createElement('div');
                        div.setAttribute('data-template-query', JSON.stringify(data['template_query']));

                        div.className = 'd-flex align-items-center';

                        const span = document.createElement('span');
                        span.className = 'flex-grow-1';
                        span.innerHTML = data[this.optionsLabel];
                        div.append(span);

                        const deleteButton = document.createElement('button');
                        deleteButton.classList.add('btn', 'btn-sm', 'btn-icon', 'px-2', 'text-danger');
                        deleteButton.type = 'button';
                        deleteButton.setAttribute('data-action', 'select-table-filter-template#deleteTemplate');
                        deleteButton.setAttribute('data-model-id', data[this.optionsValue]);
                        deleteButton.innerHTML = `
                            <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-trash" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <line x1="4" y1="7" x2="20" y2="7"></line>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                            </svg>
                        `;

                        div.append(deleteButton);

                        return div;
                    },
                    item: (data) => {
                        return `<div>` + data[this.optionsLabel] + `</div>`;
                    },
                    no_more_results: () => {
                        return `<div class="text-center">` + getMeta('dashboard-select-no-more-options-description') + `</div>`;
                    },
                    no_results: () => {
                        return `<div class="text-center">` + getMeta('dashboard-select-no-options-description') + `</div>`;
                    },
                    loading_more: () => {
                        return `<div class="text-center">` + getMeta('dashboard-select-loading') + `</div>`;
                    },
                    loading: () => {
                        return `<div class="text-center">` + getMeta('dashboard-select-loading') + `</div>`;
                    },
                },
            });

            this.select.on('change', (value) => {
                const option = this.select.getOption(value);

                if (!option || !option.dataset.templateQuery) {
                    return;
                }

                const table = this.element.closest('[data-controller="data-table"]');

                if (!table) {
                    return;
                }

                const tableController = Stimulus.getControllerForElementAndIdentifier(table, 'data-table');

                if (!tableController) {
                    return;
                }

                tableController.presetFiltersFromQuery(JSON.parse(option.dataset.templateQuery));
            });
        }
    }

    deleteTemplate(event) {
        event.stopPropagation();

        const templateID = event.currentTarget.dataset.modelId;

        if (templateID) {
            fetch(dashboardAction('settings', 'table-filter-template', 'deleteTableFiltersTemplate'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    _token: getMeta('csrf-token'),
                    template_id: templateID,
                }),
            })
                .then(async response => {
                    this.select.removeOption(templateID)
                    this.reload();

                    if (response.status !== 200) {
                        if (response.headers.get('Content-Type') === 'application/json') {
                            application.toastError(await response.json());
                        } else {
                            application.toastGeneralError();
                        }
                    }
                })
                .catch((e) => {
                    console.error(e);
                    this.reload();
                    application.toastGeneralError();
                });
        }
    }

    reload() {
        this.select.load('');
        this.select.wrapper.classList.add('preloaded');
    }

    disconnect() {
        this.select.destroy();
        super.disconnect();
    }
}
